import React, { useCallback, useMemo } from 'react';
import { StyleSheet } from 'react-native';
import { DocumentStatus, FileType, HubSectionItemStatus, HubType, Notation } from '@alucio/aws-beacon-amplify/src/models';
import {
  DNABox,
  DNAButton,
  DNACheckbox,
  DNACard,
  DNACollapsibleCard,
  DNAText,
  Iffy,
  Stack,
  util,
} from '@alucio/lux-ui/src';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import {
  DNAChip,
  DNACustomFieldChipList,
  DNADocumentStatusChip,
  DNADocumentThumbnail,
  DNAPopover,
} from 'src/components/DNA';
import { useAppSettings } from 'src/state/context/AppSettings';
import { DocumentVersionORM } from 'src/types/types'
import { useDocumentVersionORM } from 'src/state/redux/selector/document';
import { styles } from 'src/screens/Hubs/EditHub/Widgets/SharedFiles';
import { CalloutState } from 'src/state/redux/slice/contentPreviewModal';
import {
  AssociatedFileDetails,
  RowProps,
  useSharedFilesState,
} from 'src/screens/Hubs/EditHub/Widgets/SharedFilesComponents/SharedFilesProvider'
import { useHubsState } from 'src/state/context/Hubs/HubsStateProvider.proxy';
import { detectArchivedFileKeyPath } from 'src/components/SlideSelector/useThumbnailSelector';

export enum OpenContentPreviewMode {
  NEW_CALLOUT = 'New callout',
}

export const S = StyleSheet.create({
  notationGroupContainer: {
    paddingHorizontal: 24,
    paddingBottom: 24,
  },
  notationGroupCardBody: {
    padding: 24,
  },
  notationRow: {
    paddingVertical: 12,
  },
  notationRowBorder: {
    borderBottomColor: colors['color-gray-80'],
    borderBottomWidth: 1,
  },
  tooltipText: {
    color: colors['color-text-white'],
  },
  unavailableContent: {
    opacity: 0.4,
  },
})
interface DocumentRowWrapperProps extends RowProps {
  associatedContent: AssociatedFileDetails
  calloutState?: CalloutState
}

interface DocumentRowProps extends RowProps {
  documentVersionORM: DocumentVersionORM
  associatedContent?: AssociatedFileDetails
  notations?: Notation[]
  calloutState?: CalloutState
  renderInline?: boolean
}

interface DocumentBadgesProps {
  documentVersionORM: DocumentVersionORM
  isRequired: boolean
  unavailableContent: boolean
}

interface NotationGroupProps {
  documentVersionORM: DocumentVersionORM
  notations?: Notation[]
  calloutState?: CalloutState
}

const NotationGroup: React.FC<NotationGroupProps> = ({ documentVersionORM, notations, calloutState }) => {
  const { isOnline } = useAppSettings()
  const hubSharedFilesState = useSharedFilesState()
  const { handleDeleteCallout, handleOpenContentPreview } = hubSharedFilesState
  const activeNotations = notations?.filter(notation => notation.status === HubSectionItemStatus.ACTIVE) ?? []
  const visibleNotationMappedByIndex: Record<string, Notation[]> = useMemo(() => {
    const result = {}
    notations?.forEach(notation => {
      if (notation.status === HubSectionItemStatus.ACTIVE) {
        const pageNum = notation.pageId.split('_').pop() ?? ''
        result[pageNum]
          ? result[pageNum].push(notation)
          : result[pageNum] = [notation]
      }
    })
    // sort each array (key) base on creation
    Object.keys(result).forEach(pageNum => {
      result[pageNum].sort((a, b) => {
        return a.createdAt - b.createdAt
      })
    })
    return result
  }, [notations])
  const visibleNotationPageNumArray = Object.keys(visibleNotationMappedByIndex).sort((a, b) => Number(a) - Number(b))
  const unavailableContent = !isOnline && !documentVersionORM.meta.assets.isContentCached

  const openContentPreview = useCallback((pageNumber: number, notationId: string) => {
    handleOpenContentPreview(
      hubSharedFilesState,
      documentVersionORM.model.id,
      pageNumber,
      notationId,
    )
  }, [hubSharedFilesState])

  if (!visibleNotationPageNumArray.length) return null
  const title = `Callouts (${activeNotations.length})`
  return (
    <DNABox
      appearance="col"
      style={S.notationGroupContainer}
    >
      <DNACollapsibleCard
        headerTitle={title}
        isCollapsed
        customWrapperStyle={S.notationGroupCardBody}
      >
        <DNABox appearance="col">
          {visibleNotationPageNumArray.map((pageNum, index) => {
            const isLastGroup = index === visibleNotationPageNumArray.length - 1
            return visibleNotationMappedByIndex[pageNum].map((notation, index) => {
              const isLastItem = isLastGroup && index === visibleNotationMappedByIndex[pageNum].length - 1
              const pageNumber = Number(pageNum)

              const page = documentVersionORM.meta.allPages[pageNumber - 1]
              const customThumbnailKey = detectArchivedFileKeyPath(documentVersionORM.model, page)
              const onPress = () => {
                if (unavailableContent) return
                openContentPreview(pageNumber, notation.id)
              }
              return (
                <DNACard
                  key={notation.id}
                  onPress={onPress}
                  interactive="pressableAlt"
                  appearance="flat"
                  style={util.mergeStyles(
                    undefined,
                    S.notationRow,
                    [S.notationRowBorder, !isLastItem],
                    [S.unavailableContent, unavailableContent],
                  )}
                >
                  <DNABox fill childFill={1} alignY="center" spacing="md">
                    {/* Thumbnail */}
                    <Stack anchor="bottomLeft">
                      {/* Thumbnail Layer */}
                      <Stack.Layer>
                        <DNADocumentThumbnail.Hover
                          width={104}
                          height={58}
                          documentVersionORM={documentVersionORM}
                          customThumbnailKey={customThumbnailKey}
                          unavailableContent={unavailableContent}
                        />
                      </Stack.Layer>
                      {/* Page Number Layer */}
                      <Stack.Layer anchor="bottomLeft">
                        <DNAChip.Beacon children={ `#${pageNumber}` } />
                      </Stack.Layer>
                    </Stack>
                    {/* Notation Description & tags */}
                    <DNABox fill appearance="col" spacing="sm">
                      <DNAText
                        status="primary"
                        numberOfLines={2}
                        style={{ textDecorationLine: 'underline' }}
                        >
                          {notation.description || `Callout ${index + 1}`}
                        </DNAText>
                      <Iffy is={unavailableContent}>
                        <DNABox>
                          <DNAChip.Beacon
                            testID={`content-not-available-${documentVersionORM.model.title}`}
                            variant="CONTENT_UNAVAILABLE"
                          />
                        </DNABox>
                      </Iffy>
                    </DNABox>
                    {/* Buttons */}
                    <Iffy is={calloutState !== CalloutState.READONLY}>
                      <DNABox>
                        <DNAButton
                          appearance="ghost"
                          status={unavailableContent ? 'tertiary' : 'subtle'}
                          iconLeft="trash"
                          onPress={() => handleDeleteCallout(documentVersionORM.model.id, notation.id)}
                        />
                      </DNABox>
                    </Iffy>
                  </DNABox>
                </DNACard>
              )
            })
          })}
        </DNABox>
      </DNACollapsibleCard>
    </DNABox>
  )
}

const DocumentBadges: React.FC<DocumentBadgesProps> = ({ documentVersionORM, isRequired, unavailableContent }) => {
  const isArchived = documentVersionORM.relations.documentORM.model.status === DocumentStatus.ARCHIVED
  const { isLatestPublished } = documentVersionORM.meta.version
  return (
    <DNABox spacing="sm">
      <DNACustomFieldChipList documentVersionORM={documentVersionORM} />
      <Iffy is={isRequired}>
        <DNAChip.Beacon variant="REQUIRED" />
      </Iffy>
      <Iffy is={isArchived}>
        <DNADocumentStatusChip documentStatus={DocumentStatus.ARCHIVED}/>
      </Iffy>
      <Iffy is={!isLatestPublished}>
        <DNAChip.Beacon children="NEW VERSION AVAILABLE" />
      </Iffy>
      <Iffy is={unavailableContent}>
        <DNAChip.Beacon
          testID={`content-not-available-${documentVersionORM.model.title}`}
          variant="CONTENT_UNAVAILABLE"
        />
      </Iffy>
    </DNABox>
  )
}

const POPOVER_DISABLE_DEVICES: Array<'tabletPWA'> = ['tabletPWA']

export const DocumentRow: React.FC<DocumentRowProps> = ({
  documentVersionORM,
  associatedContent,
  notations,
  isLastItem,
  renderInline,
  handleDeleteItem,
  calloutState
}) => {
  const { isOnline } = useAppSettings()
  const { hubORM, showFullSizeBtn } = useHubsState()
  const hubSharedFilesState = useSharedFilesState()
  const { handleOpenContentPreview, widget } = hubSharedFilesState
  const isRequired = !!associatedContent?.isRequired
  const calloutsAvailableDocTypes = ['PDF', 'PPTX']
  const isValidDocTypeForCallouts = calloutsAvailableDocTypes.includes(documentVersionORM.model.type)
  const isAssociatedFile = !!associatedContent
  const isNotatable = isValidDocTypeForCallouts && documentVersionORM.meta.permissions.MSLExternalNotatable && !isAssociatedFile
  const isWebDoc = documentVersionORM.model.type === FileType.WEB
  const unavailableContent =
    (!isOnline && !documentVersionORM.meta.assets.isContentCached) ||
    (isWebDoc && !isOnline)

  const openContentPreview = useCallback((pageNumber?: number, mode?: OpenContentPreviewMode) => {
    if (unavailableContent) return
    handleOpenContentPreview(hubSharedFilesState, documentVersionORM.model.id, pageNumber, undefined, mode)
  }, [hubSharedFilesState, unavailableContent])

  if (!hubORM) return null
  return (
    <DNABox
      fill={!associatedContent}
      appearance="col"
      style={[{ minHeight: 113 }, isLastItem ? undefined : styles.bottomBorder]}
    >
      <DNACard
        onPress={() => openContentPreview()}
        interactive="pressableAlt"
        appearance="flat"
        style={util.mergeStyles(
          undefined,
          { minHeight: 173 },
          [{ minHeight: 113 }, showFullSizeBtn],
          [S.unavailableContent, unavailableContent],
        )}
      >
        <DNABox alignY="center" style={styles.row}>
          {/* Thumbnail */}
          <DNABox style={styles.documentThumbnailBorder}>
            <DNADocumentThumbnail.Hover
              width={120}
              height={68}
              documentVersionORM={documentVersionORM}
              unavailableContent={unavailableContent}
            />
          </DNABox>
          {/* Document title & badges */}
          <DNABox fill appearance="col" alignY="center" spacing="sm">
            <DNAText
              status="primary"
              numberOfLines={2}
              style={{ textDecorationLine: 'underline' }}
              testID="hub-shared-files-item-title"
            >
              {documentVersionORM.model.title}
            </DNAText>
            <DocumentBadges
              documentVersionORM={documentVersionORM}
              isRequired={isRequired}
              unavailableContent={unavailableContent}
            />
          </DNABox>
          {/* Buttons */}
          <DNABox alignY="center" spacing="sm">
            <Iffy is={hubORM?.model.hubType !== HubType.TEMPLATE_INSTANCE || (hubORM?.model.hubType === HubType.TEMPLATE_INSTANCE && !widget.locked)}>
              <Iffy is={isNotatable}>
                <DNAPopover placement="top" disablePopover={['tabletPWA', 'tabletWEB']}>
                  <DNAPopover.Anchor>
                    <DNAButton
                      appearance="ghost"
                      status="subtle"
                      iconLeft="plus-square"
                      onPress={() => openContentPreview(1, OpenContentPreviewMode.NEW_CALLOUT)}
                    />
                  </DNAPopover.Anchor>
                  <DNAPopover.Content>
                  <DNAText
                    style={S.tooltipText}
                    numberOfLines={1}
                  >
                    Create callout
                  </DNAText>
                  </DNAPopover.Content>
                </DNAPopover>
              </Iffy>
              <Iffy is={documentVersionORM.model.type === 'WEB'}>
                <DNAPopover
                  disablePopover={POPOVER_DISABLE_DEVICES}
                >
                  <DNAPopover.Anchor>
                    <DNACheckbox
                      onChange={() => hubSharedFilesState.handleRenderInlineToggle(documentVersionORM.model.id)}
                      checked={renderInline}
                      appearance='outline'
                      status='gray'
                    >
                      Embed
                    </DNACheckbox>
                  </DNAPopover.Anchor>
                  <DNAPopover.Content>
                    <DNAText colorMode='dark'>
                      This will embed this content directly on the page.
                    </DNAText>
                  </DNAPopover.Content>
                </DNAPopover>
              </Iffy>
              <Iffy is={!isRequired && calloutState !== CalloutState.READONLY}>
                <DNAButton
                  appearance="ghost"
                  status={unavailableContent ? 'tertiary' : 'subtle'}
                  iconLeft="trash"
                  onPress={() => handleDeleteItem(documentVersionORM.model.id, associatedContent)}
                />
              </Iffy>
            </Iffy>
          </DNABox>
        </DNABox>
      </DNACard>
      <NotationGroup documentVersionORM={documentVersionORM} notations={notations} calloutState={calloutState}/>
    </DNABox>
  )
}

export const DocumentRowWrapper: React.FC<DocumentRowWrapperProps> = ({ associatedContent, isLastItem, handleDeleteItem, calloutState }) => {
  const documentVersionORM = useDocumentVersionORM(associatedContent.versionId || '')
  if (!documentVersionORM) return null
  return (
    <DocumentRow
      documentVersionORM={documentVersionORM}
      associatedContent={associatedContent}
      notations={associatedContent.notation}
      isLastItem={isLastItem}
      handleDeleteItem={handleDeleteItem}
      calloutState={calloutState}
    />
  )
}

export default DocumentRow
